import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ZoneNameFix } from 'utils/idfParse';
import { highlightParts } from 'utils/zoneNames';

type ZoneNameFixTableProps = {
  rows: ZoneNameFix[];
  title: string;
};

const issueKey = (kind: ZoneNameFix['kind']) => {
  if (kind === 'missing') return 'IDF_LAB_NAME_FIX_MISSING';
  if (kind === 'duplicate') return 'IDF_LAB_NAME_FIX_DUPLICATE';
  return 'IDF_LAB_NAME_FIX_EXTRA';
};

const NameText = ({ name, needles }: { name: string; needles: string[] }) => (
  <>
    {highlightParts(name, needles).map((part, index) =>
      part.highlight ? (
        <Box
          key={`${part.text}-${index}`}
          component="span"
          sx={{
            background: '#fff',
            color: '#c2410c',
            fontWeight: 700,
            px: '3px',
            borderRadius: '4px',
            border: '1px solid #fdba74',
          }}
        >
          {part.text}
        </Box>
      ) : (
        <span key={`${part.text}-${index}`}>{part.text}</span>
      )
    )}
  </>
);

const ZoneNameFixTable = ({ rows, title }: ZoneNameFixTableProps) => {
  const { t, i18n } = useTranslation();
  const isHebrew = Boolean(i18n.language?.startsWith('he'));
  const en = (key: string) => t(key, { lng: 'en' });

  return (
    <Box sx={{ width: '100%', maxWidth: '640px' }}>
      <Box
        component="p"
        dir={isHebrew ? 'rtl' : 'ltr'}
        style={{ textAlign: isHebrew ? 'right' : 'left', width: '100%' }}
        sx={{
          m: 0,
          mb: 1,
          fontSize: '16px',
          fontWeight: 700,
          color: '#000',
        }}
      >
        {title}
      </Box>
      <Box
        dir="ltr"
        sx={{
          direction: 'ltr /* @noflip */',
          textAlign: 'left /* @noflip */',
          overflow: 'auto',
          maxHeight: 240,
          border: '1px solid #e7e5e4',
          borderRadius: '12px',
        }}
      >
        <Box
          component="table"
          dir="ltr"
          sx={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '14px',
            '& th, & td': {
              padding: '8px 12px',
              textAlign: 'left /* @noflip */',
              borderBottom: '1px solid #e7e5e4',
              verticalAlign: 'top',
            },
            '& th': {
              background: '#fafaf9',
              fontSize: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: '#78716c',
              position: 'sticky',
              top: 0,
            },
            '& tr:last-child td': { borderBottom: 'none' },
          }}
        >
          <thead>
            <tr>
              <th>{en('IDF_LAB_NAME_FIX_BEFORE')}</th>
              <th>{en('IDF_LAB_NAME_FIX_ISSUE')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const issue = en(issueKey(row.kind));
              const issueText =
                row.kind === 'extra' && row.removeSegs.length
                  ? `${issue} (${row.removeSegs.join(', ')})`
                  : issue;
              const tone =
                row.kind === 'missing' ? '#eff6ff' : '#fff7ed';
              return (
                <Box
                  component="tr"
                  key={`${row.kind}-${row.name}`}
                  sx={{ background: tone }}
                >
                  <Box
                    component="td"
                    sx={{
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                      wordBreak: 'break-all',
                      direction: 'ltr',
                    }}
                  >
                    <NameText name={row.name} needles={row.needles} />
                  </Box>
                  <td>{issueText}</td>
                </Box>
              );
            })}
          </tbody>
        </Box>
      </Box>
    </Box>
  );
};

export default ZoneNameFixTable;

export const formatNameFixCopyLines = (
  rows: ZoneNameFix[],
  issueLabel: (row: ZoneNameFix) => string
) => rows.map((row) => `${row.name} → ${row.fixed} (${issueLabel(row)})`);
